import {
  useCreateProperty,
  useDeleteProperty,
  useListProperties,
  useUpdateProperty,
  type PropertyIn,
  type PropertyListOut,
  type PropertyOut,
} from '@myrentalmate/api-client';
import { Link as MuiLink } from '@mui/material';
import { Link } from 'react-router';

import {
  addressFields,
  optional,
  RecordPage,
  required,
  type Column,
  type Field,
  type FormValues,
  type ListParams,
} from '../../components/RecordPage';
import { listResult } from '../../lib/apiData';
import { OccupancyMeter, StatRow, StatTile } from '../../components/Stats';
import { formatMoney, formatOccupancy } from '../../lib/format';
import { useCan } from '../../lib/permissions';
import { useDashboardSummary } from '../../lib/summary';
import { RecordExtras } from '../records/RecordExtras';

export const PROPERTY_TYPE_LABELS: Record<PropertyOut['property_type'], string> = {
  single_family: 'Single family',
  multi_family: 'Multi-family',
  commercial: 'Commercial',
  other: 'Other',
};

const FIELDS: Field[] = [
  { name: 'name', label: 'Name', required: true },
  {
    name: 'property_type',
    label: 'Type',
    type: 'select',
    required: true,
    defaultValue: 'multi_family',
    options: Object.entries(PROPERTY_TYPE_LABELS).map(([value, label]) => ({ value, label })),
  },
  ...addressFields({ required: true }),
  {
    name: 'mgmt_fee_percent',
    label: 'Management Fee (%)',
    type: 'number',
    helperText: 'Percent of rent collected that you keep. Leave empty for no fee.',
  },
  { name: 'notes', label: 'Notes', type: 'multiline' },
];

const COLUMNS: Column<PropertyListOut>[] = [
  {
    label: 'Name',
    render: (p) => (
      <MuiLink component={Link} to={`/staff/properties/${p.id}`}>
        {p.name}
      </MuiLink>
    ),
  },
  { label: 'Type', render: (p) => PROPERTY_TYPE_LABELS[p.property_type] },
  { label: 'Address', render: (p) => `${p.address_line1}, ${p.city}, ${p.state} ${p.postal_code}` },
  { label: 'Units', render: (p) => p.stats.units },
  {
    label: 'Occupancy',
    render: (p) => (
      <OccupancyMeter occupied={p.stats.occupied_units} vacant={p.stats.vacant_units} />
    ),
  },
  { label: 'Vacant', render: (p) => p.stats.vacant_units },
  { label: 'Market rent / mo', render: (p) => formatMoney(p.stats.market_rent) },
];

function toBody(values: FormValues): PropertyIn {
  return {
    name: required(values, 'name'),
    property_type: required(values, 'property_type') as PropertyIn['property_type'],
    address_line1: required(values, 'address_line1'),
    address_line2: optional(values, 'address_line2'),
    city: required(values, 'city'),
    state: required(values, 'state'),
    postal_code: required(values, 'postal_code'),
    mgmt_fee_percent: optional(values, 'mgmt_fee_percent'),
    notes: optional(values, 'notes'),
  };
}

function toValues(p: PropertyOut): FormValues {
  return {
    name: p.name,
    property_type: p.property_type,
    address_line1: p.address_line1,
    address_line2: p.address_line2 ?? '',
    city: p.city,
    state: p.state,
    postal_code: p.postal_code,
    mgmt_fee_percent: p.mgmt_fee_percent === null ? '' : String(Number(p.mgmt_fee_percent)),
    notes: p.notes ?? '',
  };
}

function useRows({ q, page, pageSize }: ListParams) {
  return listResult(useListProperties({ q, page, page_size: pageSize }));
}

export function PropertiesPage() {
  const canManage = useCan('properties.manage');
  const create = useCreateProperty();
  const update = useUpdateProperty();
  const remove = useDeleteProperty();
  const summary = useDashboardSummary();
  return (
    <RecordPage
      title="Properties"
      description="Your buildings and homes. Open one to manage its units, owners and files."
      summary={
        <StatRow>
          <StatTile label="Properties" value={summary?.properties} />
          <StatTile label="Units" value={summary?.units} />
          <StatTile
            label="Occupancy"
            value={formatOccupancy(summary?.occupied_units, summary?.vacant_units)}
          />
          <StatTile label="Vacant Units" value={summary?.vacant_units} />
        </StatRow>
      }
      singular="property"
      fields={FIELDS}
      columns={COLUMNS}
      canManage={canManage}
      useRows={useRows}
      toValues={toValues}
      create={(values) => create.mutateAsync({ data: toBody(values) })}
      update={(recordId, values) => update.mutateAsync({ recordId, data: toBody(values) })}
      remove={(recordId) => remove.mutateAsync({ recordId })}
      renderExtras={(row) => (
        <RecordExtras entityType="property" entityId={row.id} canManage={canManage} />
      )}
    />
  );
}
